<template>
	<section class="notifications-list-container">
		<template v-if="notifications && notifications.length > 0">
			<perfect-scrollbar>
				<div class="notifications-list">
					<div
						v-for="(notification, index) in notifications"
						:key="notification.id"
						class="notification-item"
						ref="notifications"
					>
						<div :class="`notification notification-${notification.id}`">
							<header class="notification-header">
								<div class="actor">
									<div class="picture">
										<img :src="notification.actorDetail.picture" />
									</div>
									<div class="detail">
										<div class="name">
											<span>{{ notification.actorDetail.name }}</span>

											<template v-if="notification.objectDetail">
												<img :src="images.activityDirection" class="direction-icon" />
												<span class="object-name">{{ notification.objectDetail.name }}</span>
											</template>
										</div>
										<div class="time">{{ getReadableDateTime(notification.createdAt) }}</div>
									</div>
								</div>
								<div class="actions">
									<img :src="images.toggleHandler" />
								</div>
							</header>
							<div class="notification-body">
								<div
									class="notification-text"
									v-html="notification.text"
									v-if="notification.verb === 'send_message'"
								></div>

								<ContentCampaignExperienceApproval
									v-if="
										notification.verb === 'experience_request_join_campaign' &&
											notification.foreignId
									"
									:id="notification.foreignId"
								/>
								<ContentPlatformContentApproval
									v-if="
										notification.verb === 'content_request_join_platform' && notification.foreignId
									"
									:id="notification.foreignId"
								/>
								<ContentGroupContentApproval
									v-if="
										notification.verb === 'content_request_join_group' && notification.foreignId
									"
									:id="notification.foreignId"
								/>
								<ContentExperienceAffiliationApproval
									v-if="
										notification.verb === 'content_request_affiliation' && notification.foreignId
									"
									:id="notification.foreignId"
								/>
							</div>
							<footer class="notification-footer">
								<div class="reply" @click="() => onClickReply(index)">
									<img :src="images.reply" />
								</div>
							</footer>
						</div>
						<textarea
							class="reply-input"
							:class="{ show: replyInputIndex === index }"
							@keydown="(e) => onReplyKeyDown(e, notification)"
						/>
					</div>
				</div>
			</perfect-scrollbar>
		</template>
		<template v-else>
			<div class="empty-results">
				<p>There is no new activity.</p>
			</div>
		</template>
	</section>
</template>

<script>
import moment from 'moment';
import ContentCampaignExperienceApproval from './ContentCampaignExperienceApproval';
import ContentPlatformContentApproval from './ContentPlatformContentApproval';
import ContentGroupContentApproval from './ContentGroupContentApproval';
import ContentExperienceAffiliationApproval from './ContentExperienceAffiliationApproval';
import toggleHandler from '@/assets/images/toggle-handler.svg';
import activityDirection from '@/assets/images/activity-direction.svg';
import reply from '@/assets/images/icons/ew-icon_reply.svg';

export default {
	name: 'NewsFeed',
	components: {
		ContentCampaignExperienceApproval,
		ContentPlatformContentApproval,
		ContentGroupContentApproval,
		ContentExperienceAffiliationApproval,
	},
	props: {
		limit: {
			type: Number,
			default: 10,
		},
		selectedSources: {
			type: Array,
			default() {
				return [];
			},
		},
	},
	watch: {
		async selectedSources(sources) {
			await this.start();
		},
	},
	data() {
		return {
			images: {
				toggleHandler,
				reply,
				activityDirection,
			},
			next: '',
			previous: '',
			notifications: [],
			replyInputIndex: null,
		};
	},
	async mounted() {
		await this.start();
	},
	methods: {
		async start() {
			this.notifications = [];
			this.next = '';
			this.previous = '';
			this.replyInputIndex = null;

			await this.loadNext();
			this.startSubscription();
		},
		async loadNext() {
			const data = await this.$repo.activity.getNotifications({
				limit: this.limit,
				next: this.next,
				actors: this.selectedSources,
			});
			const { results, next, previous } = data;
			this.notifications = this.notifications.concat(results);
			this.next = next;

			// Store previous token for first time only.
			if (!this.previous) {
				this.previous = previous;
			}
		},
		startSubscription() {
			const observable = this.$repo.activity.subscribeNewNotifications();
			observable.subscribe({
				next: (result) => {
					const { newNotification } = result.data;
					this.notifications.unshift(newNotification);
					this.$emit('newNotification', newNotification);
				},
			});
		},
		getReadableDateTime(dateString) {
			const m = moment(dateString);
			return m.fromNow();
		},
		onClickReply(index) {
			this.replyInputIndex = this.replyInputIndex === index ? null : index;
			if (this.replyInputIndex !== null && this.$refs.notifications[index]) {
				document.querySelector('.reply-input', this.$refs.notifications[index]).select();
			}
		},
		async onReplyKeyDown(e, notification) {
			if (e.keyCode === 13 && !e.shiftKey) {
				const message = e.target.value.trim();
				e.target.value = '';
				e.preventDefault();

				document.querySelector('.reply-input', this.$refs.notifications[this.replyInputIndex]).value = '';
				this.replyInputIndex = null;

				if (message) {
					let experienceId = null;
					let userId = null;
					if (notification.actor.indexOf('user:') === 0) {
						userId = notification.actorDetail.id;
						experienceId = notification.objectDetail.id;
					} else if (notification.actor.indexOf('experience:') === 0) {
						experienceId = notification.actorDetail.id;
						userId = notification.objectDetail.id;
					} else {
						return;
					}
					await this.$repo.expChat.experienceSendMessageToUser(experienceId, userId, message);
				}
			}
		},
	},
};
</script>

<style lang="scss" scoped>
.notifications-list-container {
	display: block;
	width: 100%;
	margin: 20px auto;

	.notifications-list {
		margin: 20px auto;
	}

	.notification-item {
		width: 600px;
		& + .notification-item {
			margin-top: 25px;
		}

		@media screen and (max-width: 480px) {
			width: 100%;
		}
	}

	.notification {
		background-color: #fff;
		font-size: 0.875rem;
		box-shadow: 0 3px 6px rgba(0, 0, 0, 0.16);

		.notification-header {
			display: flex;

			.actor {
				display: flex;
				flex: 1 1 auto;

				.picture {
					width: 30px;
					height: 30px;

					img {
						width: 100%;
						height: 100%;
						object-fit: cover;
					}
				}

				.detail {
					padding: 0px 10px;

					.name {
						font-weight: bold;
					}

					.direction-icon {
						display: inline-block;
						margin: 0 7px;
					}
				}
			}

			.actions {
				display: flex;
				justify-content: center;
				align-items: center;
				padding: 10px;
				cursor: pointer;
			}
		}

		.notification-body {
			padding: 10px 20px;

			img {
				max-width: 100%;
			}
		}

		.notification-footer {
			padding: 5px 15px;
			border-top: 1px solid #b9b9b9;
			min-height: 35px;

			.reply {
				display: inline-block;
				cursor: pointer;

				img {
					width: 35px;
				}
			}
		}
	}

	.reply-input {
		background-color: #fff;
		font-size: 0.75rem;
		resize: none;
		width: 100%;
		height: 0;
		margin: 0;
		box-shadow: 0 2px 3px rgba(0, 0, 0, 0.16);
		opacity: 0;
		transition: 0.3s ease all;
		outline: none;
		padding: 0;
		display: block;

		&.show {
			height: 100px;
			margin-top: 30px;
			opacity: 1;
			padding: inherit;
		}
	}

	@media screen and (min-width: $breakpoint-tablet) {
		.ps {
			height: 75vh;
		}
	}
}
</style>
